// components/TechSkillVendorDialog.tsx
import AsyncMultiSelectField from "@/components/form/components/AsyncMultiSelectField";
import InputField from "@/components/form/components/InputField";
import SelectField from "@/components/form/components/SelectField";
import TextareaField from "@/components/form/components/Textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import {
  useCreateTechSkillVendorMutation,
  useUpdateTechSkillVendorMutation,
} from "@/hooks/api/techSkillVendor.queries";
import { departmentService } from "@/services/department.service";
import { fetchIndustryOptions } from "@/services/placementVendor.service";
import {
  TechSkillVendorCreateType,
  TechSkillVendorParamsType,
  TechSkillVendorType,
  fetchTechnologiesOptions,
} from "@/services/techSkillVendor.service";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";

type TechSkillVendorDialogProps = {
  mode: "create" | "edit";
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  filters: TechSkillVendorParamsType;
  vendorId?: string;
  vendorData?: TechSkillVendorType | null;
  onCancel: () => void;
};

const techSkillVendorSchema = z.object({
  vendorName: z.string().min(1, "Vendor Name is required"),
  contactPerson: z.string().min(1, "Contact Person is required"),
  email: z.string().email("Invalid email").min(1, "Email is required"),
  phone: z.string().min(1, "Phone is required"),
  industry: z.array(z.string()).min(1, "Select at least one industry"),
  technologies: z.array(z.string()).optional(),
  status: z.enum(["active", "inactive", "pending"]),
  city: z.string().min(1, "City is required"),
  state: z.string().min(1, "State is required"),
  preferredDepartments: z.array(z.string()).optional(),
  rating: z.number().min(0).max(5).optional(),
  completedPrograms: z.number().min(0).optional(),
  description: z.string().optional(),
});

type TechSkillVendorFormType = z.infer<typeof techSkillVendorSchema>;

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
  { label: "Pending", value: "pending" },
];

const TechSkillVendorDialog = ({
  mode,
  isOpen,
  onOpenChange,
  filters,
  vendorId,
  vendorData,
  onCancel,
}: TechSkillVendorDialogProps) => {
  const createMutation = useCreateTechSkillVendorMutation(filters);
  const updateMutation = useUpdateTechSkillVendorMutation(filters);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const form = useForm<TechSkillVendorFormType>({
    resolver: zodResolver(techSkillVendorSchema),
    defaultValues: {
      vendorName: "",
      contactPerson: "",
      email: "",
      phone: "",
      industry: [],
      technologies: [],
      status: "active",
      city: "",
      state: "",
      preferredDepartments: [],
      rating: 0,
      completedPrograms: 0,
      description: "",
    },
  });

  useEffect(() => {
    if (isOpen) {
      if (mode === "edit" && vendorData) {
        setTimeout(() => {
          form.reset({
            vendorName: vendorData.vendorName,
            contactPerson: vendorData.contactPerson,
            email: vendorData.email,
            phone: vendorData.phone || "",
            industry: vendorData.industry || [],
            technologies: vendorData.technologies || [],
            status: vendorData.status,
            city: vendorData.city || "",
            state: vendorData.state || "",
            preferredDepartments: vendorData.preferredDepartments || [],
            rating: vendorData.rating || 0,
            completedPrograms: vendorData.completedPrograms || 0,
            description: vendorData.description || "",
          });
        }, 0);
      } else {
        form.reset({
          vendorName: "",
          contactPerson: "",
          email: "",
          phone: "",
          industry: [],
          technologies: [],
          status: "active",
          city: "",
          state: "",
          preferredDepartments: [],
          rating: 0,
          completedPrograms: 0,
          description: "",
        });
      }
    }
  }, [mode, vendorData, isOpen, form]);

  const handleSubmit = (values: TechSkillVendorFormType) => {
    const submissionData: TechSkillVendorCreateType = {
      ...values,
      phone: values.phone || "",
      industry: values.industry || [],
      technologies: values.technologies || [],
      city: values.city || "",
      state: values.state || "",
      preferredDepartments: values.preferredDepartments || [],
      rating: values.rating || 0,
      completedPrograms: values.completedPrograms || 0,
      description: values.description || "",
    };

    if (mode === "create") {
      createMutation.mutate(submissionData, { onSuccess: onCancel });
    } else if (mode === "edit" && vendorId) {
      updateMutation.mutate(
        { _id: vendorId, data: submissionData },
        { onSuccess: onCancel }
      );
    }
  };

  const defaultIndustryOptions =
    vendorData?.industry?.map((ind) => ({
      label: ind,
      value: ind,
    })) || [];

  const defaultTechnologiesOptions =
    vendorData?.technologies?.map((t) => ({
      label: t,
      value: t,
    })) || [];

  const defaultDepartmentOptions =
    vendorData?.preferredDepartments?.map((id) => ({
      label: `Dept ${id}`, // Placeholder
      value: id,
    })) || [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-2xl"
        onInteractOutside={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "create"
              ? "Add New Tech Skill Vendor"
              : "Edit Tech Skill Vendor"}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="grid grid-cols-1 gap-4 py-2 md:grid-cols-2"
          >
            {/* Vendor Name */}
            <InputField<TechSkillVendorFormType>
              type="text"
              name="vendorName"
              label="Vendor/Trainer Name"
              placeholder="e.g. CodeMaster Institute"
              required
              disabled={isPending}
            />

            {/* Contact Person */}
            <InputField<TechSkillVendorFormType>
              type="text"
              name="contactPerson"
              label="Contact Person"
              placeholder="e.g. David Kim"
              required
              disabled={isPending}
            />

            {/* Email */}
            <InputField<TechSkillVendorFormType>
              type="email"
              name="email"
              label="Email"
              placeholder="e.g. david@codemaster.com"
              required
              disabled={isPending}
            />

            {/* Phone */}
            <InputField<TechSkillVendorFormType>
              type="tel"
              name="phone"
              label="Phone Number"
              placeholder="e.g. 9876543210"
              inputMode="numeric"
              maxLength={10}
              required
              disabled={isPending}
            />

            {/* Expertise -> Industry as per screenshot 2 it has industry */}
            {/* But screenshot 1 had "Expertise". 
                Wait, I decided in plan "Expertise" was likely just Input or something.
                But looking closer at screenshots:
                Screenshot 1: "Expertise *" (Input field layout/look)
                Screenshot 2: "Industry *" (Select layout/look)
                If I have "Industry" (Array), "Technologies" (Array).
                Maybe "Expertise" IS "Industry"? Or "Expertise" is NOT used here?
                The USER request was "following the same pattern now @TechSkillVendorDialog.tsx".
                In `SoftSkill`, we renamed `specialization` to `expertise`.
                In `TechSkill`, `industry` seems to cover the domain (IT Services, EdTech).
                I'll leave "Expertise" OUT if it's not in the service, but I see `industry` in the service.
                Wait, in `SoftSkill`, we have `expertise` AND `preferredDepartments`.
                In `Placement`, we have `industry`.
                In `TechSkill`, I added `industry`.
                I will use `InputField` named "Expertise" if the user insists, but the service I defined has `industry`.
                Let's stick to `industry` as the main domain field for Tech Skill vendors (as per plan).
                I'll map "Expertise" label to `industry` OR just use `industry` label.
                Screenshot 2 clearly shows "Industry *".
                Screenshot 1 shows "Expertise *".
                Maybe they are different screenshots of different states?
                I'll include `Industry` (AsyncMulti) and `Technologies` (AsyncMulti).
                If "Expertise" was a loose string in screenshot 1, I might miss it.
                But `TechSkill` usually implies specific technologies.
                `Industry` is good coverage.
            */}

            {/* Industry - Async Multi Select */}
            <AsyncMultiSelectField<TechSkillVendorFormType>
              name="industry"
              label="Industry"
              placeholder="Select industries"
              fetchOptions={fetchIndustryOptions}
              queryKey={["industry"]}
              defaultOptions={defaultIndustryOptions}
              required
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Status */}
            <SelectField<TechSkillVendorFormType>
              name="status"
              label="Status"
              options={STATUS_OPTIONS}
              required
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Technologies */}
            <AsyncMultiSelectField<TechSkillVendorFormType>
              name="technologies"
              label="Technologies/Tools Covered"
              placeholder="Select technologies and tools"
              fetchOptions={fetchTechnologiesOptions}
              queryKey={["technologies"]}
              defaultOptions={defaultTechnologiesOptions}
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* City */}
            <InputField<TechSkillVendorFormType>
              type="text"
              name="city"
              label="City"
              placeholder="e.g. Bangalore"
              required
              disabled={isPending}
            />

            {/* State */}
            <InputField<TechSkillVendorFormType>
              type="text"
              name="state"
              label="State"
              placeholder="e.g. Karnataka"
              required
              disabled={isPending}
            />

            {/* Preferred Departments - Async Multi Select */}
            <AsyncMultiSelectField<TechSkillVendorFormType>
              name="preferredDepartments"
              label="Preferred Departments"
              placeholder="Select preferred departments"
              fetchOptions={departmentService.fetchDepartmentOptions}
              queryKey={["departments"]}
              defaultOptions={defaultDepartmentOptions}
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            {/* Rating */}
            <InputField<TechSkillVendorFormType>
              type="number"
              name="rating"
              label="Rating"
              placeholder="e.g. 4.5"
              step="0.1"
              disabled={isPending}
            />

            {/* Completed Programs */}
            <InputField<TechSkillVendorFormType>
              type="number"
              name="completedPrograms"
              label="Completed Programs"
              placeholder="e.g. 10"
              disabled={isPending}
            />

            {/* Description */}
            <TextareaField<TechSkillVendorFormType>
              name="description"
              label="Description"
              placeholder="Brief description about the vendor/trainer"
              disabled={isPending}
              className={{ itemClass: "md:col-span-2" }}
            />

            <DialogFooter className="md:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === "create"
                    ? "Adding..."
                    : "Saving..."
                  : mode === "create"
                    ? "Create Vendor"
                    : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default TechSkillVendorDialog;
